import React, { useState, useEffect } from 'react';
import { useLang } from '../i18n/LanguageContext';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { tr } = useLang();
  
  const mediaItems = [
    { type: 'image', src: '/images/water1.jpg', alt: 'Water Resources 1' },
    { type: 'image', src: '/images/water2.jpg', alt: 'Water Resources 2' },
    { type: 'image', src: '/images/water3.jpg', alt: 'Water Resources 3' },
    { type: 'image', src: '/images/pond1.png', alt: 'Water Resources 4' }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % mediaItems.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [mediaItems.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % mediaItems.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Source+Sans+Pro:wght@300;400;600;700&display=swap');
        
        @keyframes fadeInUp {
          from { 
            opacity: 0; 
            transform: translateY(30px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        .fade-in-up {
          animation: fadeInUp 1s ease-out;
        }
        
        .fade-in-up-delay {
          animation: fadeInUp 1.2s ease-out 0.2s both;
        }
        
        .minimal-font {
          font-family: 'Source Sans Pro', 'Inter', system-ui, sans-serif;
        }
      `}</style>
      
      <section className="relative h-screen overflow-hidden">
        {/* Background Images */}
        <div className="absolute inset-0">
          {mediaItems.map((media, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={media.src}
                alt={media.alt}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080' viewBox='0 0 1920 1080'%3E%3Cdefs%3E%3ClinearGradient id='bg' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23f8fafc'/%3E%3Cstop offset='100%25' style='stop-color:%23e2e8f0'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='1920' height='1080' fill='url(%23bg)'/%3E%3C/svg%3E";
                }}
              />
            </div>
          ))}
          {/* Minimal overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20"></div>
        </div>

        {/* Simple Navigation */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-30 w-10 h-10 bg-black/20 hover:bg-black/30 text-white rounded-full transition-colors duration-200 flex items-center justify-center"
          aria-label="Previous"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 w-10 h-10 bg-black/20 hover:bg-black/30 text-white rounded-full transition-colors duration-200 flex items-center justify-center"
          aria-label="Next"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-30 flex space-x-2">
          {mediaItems.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === currentSlide ? 'bg-white w-6' : 'bg-white/60'
              }`}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Content - Left Aligned */}
        <div className="relative z-20 h-full flex items-center">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="max-w-4xl">
              <div className="minimal-font text-white space-y-4">
                {/* Banner heading stays ஏரி இணையதளம் in both languages (per client). */}
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold fade-in-up leading-tight">
                  ஏரி இணையதளம்
                </h1>
                <p className="text-lg sm:text-xl font-medium tracking-widest uppercase opacity-90 fade-in-up">
                  {tr('ERI Website', 'தமிழ்நாடு அரசு')}
                </p>

                <div className="w-16 h-0.5 bg-brand-accent fade-in-up-delay"></div>

                <p className="text-lg sm:text-xl lg:text-2xl font-light leading-relaxed opacity-95 fade-in-up-delay max-w-3xl">
                  {tr(
                    'Website for Issuing Permission to Non-Governmental Organisations (NGOs) for the Restoration of Water Bodies',
                    'நீர்நிலைகளைப் புனரமைப்பதற்கு அரசு சாரா நிறுவனங்களுக்கு (NGOs) அனுமதி வழங்குவதற்கான இணையதளம்'
                  )}
                </p>
                <p className="inline-block bg-black/30 px-4 py-2 text-sm sm:text-base font-semibold fade-in-up-delay">
                  {tr(
                    'One Platform • Easy Application • Faster Approvals • Transparent Monitoring',
                    'ஒரே தளம் • எளிய விண்ணப்பம் • விரைவான அனுமதி • வெளிப்படையான கண்காணிப்பு'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;